import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { App as AntdApp, Button, Card, Empty, Flex, Table, Tabs, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { LeftOutlined, MailOutlined, ReloadOutlined, WalletOutlined } from '@ant-design/icons'
import { Link, useParams } from 'react-router-dom'
import type { AccountTransaction, AccountTransactionListType, AccountTransactionsPage, ApiKeyRecord } from '../../shared/services/api'
import { getApiService } from '../../shared/services/api'
import { TopUpAccountModal } from '../../features/accounts/components/TopUpAccountModal'
import { MetricCard } from '../../shared/ui/MetricCard'
import { PageHeader } from '../../shared/ui/PageHeader'
import { formatMoney, formatSignedMoney } from '../../shared/utils/money'
import { formatInAppTimezone } from '../../shared/utils/timezone'
import { pluralRu } from '../../shared/utils/plural'

const PAGE_SIZE = 25
const types: AccountTransactionListType[] = ['all', 'topups', 'charges']

export function AccountTransactionsPage() {
  const { message } = AntdApp.useApp()
  const { id } = useParams()
  const accountId = Number(id)
  const api = useMemo(() => getApiService(), [])
  const [data, setData] = useState<AccountTransactionsPage | null>(null)
  const [loading, setLoading] = useState(false)
  const [activeType, setActiveType] = useState<AccountTransactionListType>('all')
  const [pageByType, setPageByType] = useState<Record<AccountTransactionListType, number>>({ all: 1, topups: 1, charges: 1 })
  const [topUpOpen, setTopUpOpen] = useState(false)
  const requestId = useRef(0)

  const load = useCallback(async () => {
    if (!Number.isInteger(accountId) || accountId < 1) return
    const currentRequest = requestId.current + 1
    requestId.current = currentRequest
    setLoading(true)
    try {
      const response = await api.apiKeyTransactions(accountId, { type: activeType, page: pageByType[activeType], pageSize: PAGE_SIZE })
      if (requestId.current === currentRequest) setData(response.data)
    } catch {
      if (requestId.current === currentRequest) message.error('Не удалось загрузить операции счёта')
    } finally {
      if (requestId.current === currentRequest) setLoading(false)
    }
  }, [accountId, activeType, api, message, pageByType])

  useEffect(() => { void load() }, [load])

  const accountForTopUp: ApiKeyRecord | null = data ? {
    id: data.account.id,
    name: data.account.name,
    scopes: [],
    allowedIps: [],
    isActive: data.account.isActive,
    metadata: {},
    expiresAt: null,
    lastUsedAt: null,
    balance: data.balance,
  } : null

  const columns: ColumnsType<AccountTransaction> = [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      width: 180,
      render: (value: string) => formatInAppTimezone(value, 'DD.MM.YYYY HH:mm:ss'),
    },
    {
      title: 'Операция',
      dataIndex: 'operation_type',
      width: 210,
      render: (operationType: number, transaction) => operationType === 2
        ? <Flex align="center" gap={8}><WalletOutlined className="transaction-kind-icon transaction-kind-topup" /><Typography.Text>Пополнение</Typography.Text></Flex>
        : <Link className="transaction-email-link" to={`/emails?open=${transaction.email_id}`}><MailOutlined /> Письмо №{transaction.email_id}</Link>,
    },
    {
      title: 'Состояние',
      dataIndex: 'status',
      width: 145,
      render: (status: number, transaction) => <TransactionStatus status={status} operationType={transaction.operation_type} />,
    },
    {
      title: 'Основание',
      dataIndex: 'comment',
      minWidth: 260,
      render: (comment: string | null, transaction) => (
        <Flex vertical gap={1}>
          <Typography.Text>{transaction.operation_type === 2 ? (comment || 'Ручное пополнение') : 'Стоимость отправки письма'}</Typography.Text>
          <Typography.Text type="secondary">Проводка №{transaction.id}</Typography.Text>
        </Flex>
      ),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      align: 'right',
      width: 180,
      render: (amount: number | string, transaction) => (
        <Typography.Text
          strong
          className={`account-transaction-amount ${transaction.status === 1 ? 'account-transaction-amount-cancelled' : Number(amount) > 0 ? 'account-transaction-amount-success' : transaction.status === 0 ? 'account-transaction-amount-reserved' : 'account-transaction-amount-danger'}`}
        >
          {formatSignedMoney(amount, transaction.currency)}
        </Typography.Text>
      ),
    },
  ]

  const summary = data?.summary
  const currency = data?.balance.currency ?? 'USD'
  const tabs = [
    { key: 'all', label: `Все (${(summary?.topupsCount ?? 0) + (summary?.chargesCount ?? 0)})` },
    { key: 'topups', label: `Пополнения (${summary?.topupsCount ?? 0})` },
    { key: 'charges', label: `Списания (${summary?.chargesCount ?? 0})` },
  ]

  return (
    <Flex className="account-transactions-page" vertical gap="middle">
      <Link className="account-back-action" to="/accounts"><Button icon={<LeftOutlined />}>К счетам</Button></Link>
      <PageHeader
        title={data ? data.account.name : `Счёт №${id}`}
        description={data ? `Операции расчётного счёта №${data.account.id}` : 'История пополнений и списаний'}
        icon={<WalletOutlined />}
        extra={(
          <Flex gap={8} wrap>
            <Button icon={<ReloadOutlined />} loading={loading} onClick={() => void load()}>Обновить</Button>
            {data?.account.isActive && <Button type="primary" icon={<WalletOutlined />} onClick={() => setTopUpOpen(true)}>Пополнить</Button>}
          </Flex>
        )}
      />

      <div className="metric-grid">
        <MetricCard title="Доступно" value={formatMoney(data?.balance.available ?? 0, currency)} detail="Для новых писем" tone="green" />
        <MetricCard title="Пополнено" value={formatMoney(summary?.topupsTotal ?? 0, currency)} detail={`${summary?.topupsCount ?? 0} ${pluralRu(summary?.topupsCount ?? 0, 'операция', 'операции', 'операций')}`} tone="blue" />
        <MetricCard title="Списано" value={formatMoney(summary?.chargesTotal ?? 0, currency)} detail={`${summary?.chargesCount ?? 0} ${pluralRu(summary?.chargesCount ?? 0, 'отправка', 'отправки', 'отправок')}, отменено ${summary?.cancelledCount ?? 0}`} tone="red" />
        <MetricCard title="В резерве" value={formatMoney(summary?.reservedTotal ?? 0, currency)} detail={`${summary?.reservedCount ?? 0} ${pluralRu(summary?.reservedCount ?? 0, 'письмо ожидает', 'письма ожидают', 'писем ожидают')} результата`} tone="amber" />
      </div>

      <Card className="table-card account-transactions-workbench">
        <Tabs
          className="account-transactions-tabs"
          activeKey={activeType}
          items={tabs}
          onChange={(key) => {
            if (types.includes(key as AccountTransactionListType)) setActiveType(key as AccountTransactionListType)
          }}
        />
        <Table<AccountTransaction>
          rowKey="id"
          className="account-transactions-table"
          columns={columns}
          dataSource={data?.transactions ?? []}
          loading={loading}
          scroll={{ x: 975 }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Операций пока нет" /> }}
          pagination={{
            current: data?.page ?? pageByType[activeType],
            pageSize: data?.pageSize ?? PAGE_SIZE,
            total: data?.count ?? 0,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0]}–${range[1]} из ${total}`,
            onChange: (page) => setPageByType((current) => ({ ...current, [activeType]: page })),
          }}
        />
      </Card>

      <TopUpAccountModal
        account={topUpOpen ? accountForTopUp : null}
        onCancel={() => setTopUpOpen(false)}
        onSubmit={async (selectedAccountId, amount, comment) => {
          try {
            await api.topUpApiKey(selectedAccountId, amount, comment)
            message.success('Счёт пополнен')
            setTopUpOpen(false)
            await load()
          } catch (error) {
            message.error('Не удалось пополнить счёт')
            throw error
          }
        }}
      />
    </Flex>
  )
}

function TransactionStatus({ status, operationType }: { status: number; operationType: number }) {
  if (status === 1) return <Tag>Отменено</Tag>
  if (status === 0) return <Tag color="processing">В резерве</Tag>
  return <Tag color="success">{operationType === 2 ? 'Зачислено' : 'Списано'}</Tag>
}
